import axios from 'axios';
import { setup } from 'axios-cache-adapter';
import { getToken, setSyncRequests } from '../services/auth';
import localforage from 'localforage';
import { Notification } from 'notification/notification';
import { handleSendGeneric } from 'services/generic';
import { uuidv4 } from 'utility/generic';
import { convertToFloat } from 'utility/generic';

const API = process.env.REACT_APP_SERVER_URL;

// Cria uma instância do cache
const cache = setup({
  maxAge: 18 * 60 * 60 * 1000, // Tempo máximo de cache em milissegundos (aqui, 24 horas)
  store: localforage, // Usa o localforage como o mecanismo de armazenamento
});

// Cria uma instância do Axios com o cache configurado
const api = axios.create({
  baseURL: API,
  adapter: cache.adapter, // Usa o adaptador de cache do axios-cache-adapter
});

// Intercepta as requisições para adicionar o token de autenticação
api.interceptors.request.use(async (config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Função para verificar se há conexão com a internet
const isOnline = () => {
  return window.navigator.onLine;
};

// Sobrescreve a função `request` para buscar respostas do cache quando não há conexão ou ocorre erro de conexão
const originalRequest = api.request;

api.request = async (config) => {
  try {
    const response = await originalRequest(config);
    return response;
  } catch (error) {
    if (!isOnline() || error.message === 'Network Error') {
      const cachedResponse = await localforage.getItem(config.url);
      if (cachedResponse) {
        return { data: cachedResponse };
      }
    }
    throw error;
  }
};

// Intercepta as respostas para armazenar em cache
api.interceptors.response.use(
  (response) => {
    if (isOnline()) {
      localforage.setItem(response.config.url, response.data); // Armazena a resposta em cache
    }
    return response;
  },
  async (error) => {
    if (error.message === 'Network Error') {
      if (
        error.config.url === '/v1/visit-record' &&
        error.config.method === 'post'
      ) {
        return { data: {}, status: 200 };
      }
      if (
        error.config.url.includes('/v1/sale/execute') &&
        error.config.method === 'get'
      ) {
        const valorNumerico = error.config.url.split('/v1/sale/execute/')[1];
        const cachedResponse = await localforage.getItem(
          '/v1/client/' + valorNumerico,
        );
        if (cachedResponse) {
          return { data: cachedResponse };
        }
      }
      if (error.config.url === 'whatsapp/v1/messages' && error.config.method === 'post') {
        const bodyData = error.config.data;
        const syncRequests = (await localforage.getItem('syncRequests')) || [];
        syncRequests.push({
          url: error.config.url,
          method: 'POST',
          body: bodyData,
        });
        setSyncRequests(syncRequests.length);
        await localforage.setItem('syncRequests', syncRequests);
        return { data: { id: uuidv4(), status: 'PENDING' }, status: 200 };
      }
      if (
        error.config.url === '/v1/sale' ||
        error.config.url === '/v1/sale/execute' ||
        error.config.url.includes('/v1/client/state') ||
        error.config.url === '/v1/visit-record'
      ) {
        const bodyData = error.config.data;
        if (error.config.url === '/v1/sale') {
          const bodyDataJson = JSON.parse(bodyData);
          let clientToBeUpdate =
            bodyDataJson.client === null
              ? { ...bodyDataJson.client.client }
              : { ...bodyDataJson.client };

          let clientOnCache = await localforage.getItem(
            '/v1/client/' + bodyDataJson.client.id,
            clientToBeUpdate,
          );
          if (clientOnCache !== undefined) {
            clientToBeUpdate = clientOnCache;
          }
          bodyDataJson.productList.forEach((prod) => {
            const itemFromStock = clientToBeUpdate.stocks.find(
              (item) => item.name === prod.name,
            );
            if (itemFromStock !== undefined) {
              const debtorWithoutCommision =
                convertToFloat(prod.value) * parseFloat(prod.quantity);
              const newDebtorWithAppliedCommission =
                debtorWithoutCommision -
                debtorWithoutCommision *
                  (parseFloat(clientOnCache.commission) / 100);
              itemFromStock.quantity += parseFloat(prod.quantity);
              itemFromStock.debtor =
                convertToFloat(itemFromStock.debtor) +
                newDebtorWithAppliedCommission;
            } else {
              const debtorWithoutCommision =
                convertToFloat(prod.value) * parseFloat(prod.quantity);
              const newDebtorWithAppliedCommission =
                debtorWithoutCommision -
                debtorWithoutCommision *
                  (parseFloat(clientOnCache.commission) / 100);

              clientToBeUpdate.stocks.push({
                ...prod.stock,
                sold: 0,
                returned: 0,
                amount_paid: 0,
                debtor: newDebtorWithAppliedCommission,
              });
            }
          });

          await localforage.setItem(
            '/v1/client/' + bodyDataJson.client.id,
            clientToBeUpdate,
          );
          const syncRequests =
            (await localforage.getItem('syncRequests')) || [];
          syncRequests.push({
            url: error.config.url,
            method: error.config.method?.toUpperCase() || 'POST',
            body: bodyData,
          });
          setSyncRequests(syncRequests.length);
          await localforage.setItem('syncRequests', syncRequests);
          return { data: { client: clientToBeUpdate }, status: 200 };
        }
        if (error.config.url === '/v1/sale/execute') {
          const bodyDataJson = JSON.parse(bodyData);
          const { client, productList } = bodyDataJson;
          console.table(bodyDataJson);

          productList.forEach((prod) => {
            const itemFromStock = client.stocks.find(
              (item) => item.name === prod.name,
            );
            if (itemFromStock !== undefined) {
              const debtorWithoutCommision =
                convertToFloat(prod.value) * parseFloat(prod.sold);
              const newDebtorWithAppliedCommission =
                debtorWithoutCommision -
                debtorWithoutCommision * (parseFloat(client.commission) / 100);
              itemFromStock.quantity =
                parseFloat(prod.quantity) - (prod.sold + prod.returned);

              const debtorPaid =
                convertToFloat(prod.debtor) - convertToFloat(prod.amount_paid);

              itemFromStock.debtor =
                prod.sold === 0 || prod.sold === prod.quantity
                  ? debtorPaid.toFixed(2)
                  : (
                      newDebtorWithAppliedCommission -
                      convertToFloat(prod.amount_paid)
                    ).toFixed(2);

              itemFromStock.returned = 0;
              itemFromStock.sold = 0;
            }
          });
          await localforage.setItem('/v1/client/' + client.id, client);

          const syncRequests =
            (await localforage.getItem('syncRequests')) || [];
          syncRequests.push({
            url: error.config.url,
            method: error.config.method?.toUpperCase() || 'POST',
            body: bodyData,
          });
          setSyncRequests(syncRequests.length);
          await localforage.setItem('syncRequests', syncRequests);
          return {
            data: { id: uuidv4(), client: client },
            status: 200,
          };
        }
        const syncRequests = (await localforage.getItem('syncRequests')) || [];
        syncRequests.push({
          url: error.config.url,
          method: error.config.method?.toUpperCase() || 'POST',
          body: bodyData,
        });
        setSyncRequests(syncRequests.length);
        await localforage.setItem('syncRequests', syncRequests);
        return { data: {}, status: 200 };
      }
      const cachedResponse = await localforage.getItem(error.config.url);

      if (cachedResponse) {
        return { data: cachedResponse };
      }
    }
    return Promise.reject(error);
  },
);

export const getSync = async () => {
  const syncRequests = (await localforage.getItem('syncRequests')) || [];
  return syncRequests.length;
};

async function sendRequest(request) {
  const response = await fetch(API + request.url, {
    method: request.method || 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: request.body,
  });
  if (response.ok) {
    console.log(`Successfully sent data to ${request.url}`);
    return true;
  } else {
    console.log(
      `Failed to send data to ${request.url}: ${response.status} ${response.statusText}`,
    );
    return false;
  }
}

async function uploadOfflineAttachment(localKey) {
  const stored = await localforage.getItem(localKey);
  if (!stored) return null;
  const { base64, filename, mimeType } = stored;
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
  const blob = new Blob([ab], { type: mimeType });
  const file = new File([blob], filename, { type: mimeType });
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(API + 'whatsapp/v1/upload', {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: formData,
  });
  if (!response.ok) return null;
  const data = await response.json();
  await localforage.removeItem(localKey);
  return data;
}

async function syncRequestsHandler(syncRequests) {
  for (let i = 0; i < syncRequests.length; i++) {
    const request = syncRequests[i];
    try {
      // Mensagem WhatsApp com anexo salvo offline: precisa fazer upload antes de enviar
      if (request.url === 'whatsapp/v1/messages' && request.body) {
        const bodyJson = JSON.parse(request.body);
        if (bodyJson.localAttachmentKey) {
          const uploaded = await uploadOfflineAttachment(bodyJson.localAttachmentKey);
          if (!uploaded) {
            Notification(500, 'Erro ao fazer upload do anexo offline.');
            continue;
          }
          bodyJson.attachmentUrl  = uploaded.url;
          bodyJson.attachmentType = uploaded.mimeType;
          bodyJson.attachmentName = uploaded.filename;
          delete bodyJson.localAttachmentKey;
          request.body = JSON.stringify(bodyJson);
        }
      }

      const isSyncSuccessful = await sendRequest(request);
      if (isSyncSuccessful) {
        syncRequests.splice(i, 1);
        await localforage.setItem('syncRequests', syncRequests);
        Notification(200, 'Dado sincronizado com sucesso!');
        i--;
      } else {
        Notification(500, 'Erro ao sincronizar dados, tente novamente.');
        handleSendGeneric('/v1/log/save-log', 'POST', {
          data: { path: request.url, raw: request.body },
        });
      }
    } catch (error) {
      console.log(`Error sending data to:`, error);
      console.log(request);
    }
  }
}

export const syncToSend = async () => {
  const syncRequests = (await localforage.getItem('syncRequests')) || [];

  if (!isOnline()) {
    Notification(500, 'Conecte a internet para inicar a sincronização');
    return;
  }

  await syncRequestsHandler(syncRequests);

  if (syncRequests.length === 0) {
    setSyncRequests(syncRequests.length);
    Notification(200, 'Nenhum dado para sincronizar');
    setTimeout(() => window.location.reload(), 2000);
  }
};

export const updateClientCache = async (statusClient, client) => {
  const clientRaw = await localforage.getItem('/v1/client/' + client.id);
  await localforage.setItem('/v1/client/' + client.id, {
    ...clientRaw,
    stateClientEnum: statusClient,
  });
};

export default api;
